import { Stack } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { Building07Icon } from '@/components/icons/Building07Icon';
import { AIOnboardingModalVariant } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { groupSuggestionsByEntityOrTestamentaryEntityID } from '@/modules/aiSuggestions/aiSuggestions.utils';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { BUSINESS_ENTITY_KINDS } from '@/modules/entities/entities.constants';
import {
  EstateOnboardingSuggestionGroup,
  isEstateOnboardingReviewCardSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { groupEstateOnboardingSuggestionsByGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { EstateBusinessEntityCard } from '@/modules/estate/EstateBusinesses/EstateBusinessEntityCard';
import { sumGrantorControlledValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { pluralize } from '@/utils/formatting/strings';

import { useReviewSuggestionCategoryData } from './ReviewSuggestion.hooks';
import { ReviewSuggestionLoadingState } from './ReviewSuggestion.LoadingState';
import { useReviewSuggestionOnboardingModal } from './ReviewSuggestion.OnboardingModal';
import { ReviewSuggestionProvider } from './ReviewSuggestion.provider';
import {
  ReviewSuggestionEntityGroupHeader,
  ReviewSuggestionSectionHeader,
} from './ReviewSuggestion.SectionHeader';
import { getEstateReviewSuggestions } from './ReviewSuggestions.utils';

export function BusinessReviewSuggestions() {
  const editEntityTrowser = useEditEntityTrowser();
  const data = useReviewSuggestionCategoryData({
    entityKinds: BUSINESS_ENTITY_KINDS,
    includeOnboardingSuggestions: true,
    includeTestamentaryEntities: false,
  });
  const estateReview = useMemo(
    () =>
      getEstateReviewSuggestions({
        entities: data.entities,
        testamentaryEntities: [],
        suggestionsByEntityID: groupSuggestionsByEntityOrTestamentaryEntityID(
          data.suggestions
        ),
      }),
    [data.entities, data.suggestions]
  );
  const newSuggestions = useMemo(
    () =>
      groupEstateOnboardingSuggestionsByGroup(
        data.onboardingSuggestions.filter(
          isEstateOnboardingReviewCardSuggestion
        ),
        compact(data.entities)
      )[EstateOnboardingSuggestionGroup.BusinessEntities],
    [data.entities, data.onboardingSuggestions]
  );
  const onboardingModal = useReviewSuggestionOnboardingModal({
    suggestions: newSuggestions,
    variant: AIOnboardingModalVariant.BUSINESS_ENTITIES,
  });
  if (data.loading && data.entities.length === 0)
    return <ReviewSuggestionLoadingState />;

  return (
    <ReviewSuggestionProvider {...data}>
      <Stack spacing={3}>
        {newSuggestions.length > 0 && (
          <Stack spacing={2}>
            <ReviewSuggestionSectionHeader
              title={pluralize({
                count: newSuggestions.length,
                singular: 'newly identified business',
                plural: 'newly identified businesses',
              })}
              description="The following businesses were identified in client documents, but have not been created yet."
            />
            <AISuggestionReviewCard
              variant={AIOnboardingModalVariant.BUSINESS_ENTITIES}
              displayMode="expanded-list"
              suggestionDisplayText={{
                singular: 'business',
                plural: 'businesses',
              }}
              suggestions={newSuggestions}
              onOpenAIOnboardingModal={onboardingModal.open}
              onSuggestionCreated={data.refetch}
              showProcessingBanner={false}
            />
          </Stack>
        )}
        {estateReview.businesses.length > 0 && (
          <Stack spacing={2}>
            <ReviewSuggestionSectionHeader
              title={pluralize({
                count: estateReview.counts.businesses,
                singular: 'suggestion for an existing business',
                plural: 'suggestions for existing businesses',
              })}
              description="The following existing businesses have outstanding suggestions."
            />
            <Stack spacing={1}>
              <ReviewSuggestionEntityGroupHeader
                title="Businesses"
                subtitle={formatCurrencyNoDecimals(
                  sumGrantorControlledValue(estateReview.businesses)
                )}
                icon={Building07Icon}
                iconColor={COLORS.NAVY[700]}
                backgroundColor={COLORS.NAVY[100]}
              />
              {estateReview.businesses.map((business) => (
                <EstateBusinessEntityCard
                  key={business.id}
                  business={business}
                  onEditEntitySection={(entityId, section) =>
                    editEntityTrowser.openForEntity(entityId, { section })
                  }
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
      {onboardingModal.modal}
    </ReviewSuggestionProvider>
  );
}
